import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import Input from '@mui/joy/Input';
import Textarea from '@mui/joy/Textarea';
import Stack from '@mui/joy/Stack';
import Alert from '@mui/joy/Alert';
import Table from '@mui/joy/Table';
import IconButton from '@mui/joy/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { brandsApi } from '../../api/brands.api';
import { CreateBrandDto } from '../../types/brand';

const emptyBrand = (): CreateBrandDto => ({ name: '', description: '', logo: '' });

const BulkBrandCreator: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [brands, setBrands] = useState<CreateBrandDto[]>(() => [emptyBrand()]);
  const [results, setResults] = useState<{ success: number; errors: number }>({
    success: 0,
    errors: 0,
  });
  const [showResults, setShowResults] = useState(false);

  const updateBrand = useCallback((index: number, patch: Partial<CreateBrandDto>) => {
    setBrands((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...patch };
      return next;
    });
  }, []);

  const handleAddRow = () => {
    setBrands((prev) => [...prev, emptyBrand()]);
  };

  const handleRemoveBrand = (index: number) => {
    setBrands((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validBrands = brands.filter((brand) => brand.name.trim() !== '');
    if (validBrands.length === 0) {
      alert('Por favor, ingresa al menos una marca válida.');
      return;
    }
    setLoading(true);
    setShowResults(false);
    try {
      const result = await brandsApi.createBulk(validBrands);
      setResults({
        success: result.success.length,
        errors: result.errors.length,
      });
      setShowResults(true);
    } catch (error) {
      console.error('Error al crear marcas:', error);
      setResults({ success: 0, errors: validBrands.length });
      setShowResults(true);
    } finally {
      setLoading(false);
    }
  };

  const handleImportFromCsv = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target?.result) {
        const content = event.target.result.toString();
        const lines = content.split('\n').filter((l) => l.trim());
        const dataLines = lines.length > 1 && /^nombre/i.test(lines[0].split(',')[0]?.trim() ?? '')
          ? lines.slice(1)
          : lines;
        const importedBrands: CreateBrandDto[] = [];
        dataLines.forEach((line) => {
          const [name, description = '', logo = ''] = line.split(',').map((item) => item.trim());
          if (name) {
            importedBrands.push({ name, description, logo });
          }
        });
        if (importedBrands.length > 0) {
          setBrands(importedBrands);
        } else {
          alert('No se encontraron marcas válidas en el archivo.');
        }
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const downloadTemplate = () => {
    const csvContent =
      'Nombre,Descripción,URL del Logo\nMarca Ejemplo 1,Descripción de ejemplo 1,https://ejemplo.com/logo1.png\nMarca Ejemplo 2,Descripción de ejemplo 2,https://ejemplo.com/logo2.png';
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'plantilla_marcas.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const bulkTableSx = {
    tableLayout: 'fixed',
    width: '100%',
    minWidth: 720,
    '& thead th': {
      bgcolor: 'background.surface',
      zIndex: 1,
      whiteSpace: 'nowrap',
      fontWeight: 'lg',
      fontSize: 'xs',
      py: 0.5,
      px: 1,
      verticalAlign: 'middle',
    },
    '& td': { verticalAlign: 'middle', py: 0.5, px: 1 },
  };

  const inputSlotPadding = { sx: { py: '10px', px: '12px', minHeight: 40, boxSizing: 'border-box' as const } };
  const textareaSlotPadding = { sx: { py: '10px', px: '12px', minHeight: 40, maxHeight: 72, overflowY: 'auto' } };

  return (
    <Box sx={{ p: 2, maxWidth: 'min(100%, 1200px)', mx: 'auto', width: '100%' }}>
      <Typography level="h2" sx={{ mb: 2 }}>
        Creación masiva de marcas
      </Typography>

      {showResults && (
        <Alert
          color={results.errors === 0 ? 'success' : 'warning'}
          sx={{ mb: 2 }}
          endDecorator={
            <Button size="sm" onClick={() => navigate('/brands')}>
              Ver todas las marcas
            </Button>
          }
        >
          <Typography level="title-sm">Resultados</Typography>
          <Typography level="body-sm">{results.success} marcas creadas correctamente</Typography>
          {results.errors > 0 ? (
            <Typography level="body-sm">{results.errors} con errores</Typography>
          ) : null}
        </Alert>
      )}

      <Sheet variant="outlined" sx={{ p: 3 }}>
        <Stack spacing={3}>
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography level="title-md">Importar desde CSV</Typography>
              <Button size="sm" variant="plain" color="primary" onClick={downloadTemplate}>
                Descargar plantilla
              </Button>
            </Stack>
            <Button component="label" variant="outlined" color="neutral">
              Elegir archivo CSV
              <input type="file" accept=".csv" hidden onChange={handleImportFromCsv} />
            </Button>
            <Typography level="body-xs" sx={{ color: 'neutral.500', mt: 1 }}>
              Formato: Nombre, Descripción, URL del Logo (primera fila puede ser encabezado)
            </Typography>
          </Box>

          <Box component="form" onSubmit={handleSubmit}>
            <Typography level="title-md" sx={{ mb: 1 }}>
              Edición en tabla
            </Typography>
            <Box
              sx={{
                overflowX: 'auto',
                borderRadius: 'sm',
                border: '1px solid',
                borderColor: 'divider',
                maxHeight: 'min(70vh, 720px)',
                overflowY: 'auto',
              }}
            >
              <Table stickyHeader size="sm" borderAxis="bothBetween" sx={bulkTableSx}>
                <colgroup>
                  <col style={{ width: 44 }} />
                  <col style={{ width: '22%' }} />
                  <col style={{ width: '28%' }} />
                  <col style={{ width: '26%' }} />
                  <col style={{ width: 52 }} />
                  <col style={{ width: 52 }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Nombre</th>
                    <th>Descripción</th>
                    <th>URL del logo</th>
                    <th>Vista</th>
                    <th aria-label="Eliminar" />
                  </tr>
                </thead>
                <tbody>
                  {brands.length === 0 ? (
                    <tr>
                      <td colSpan={6}>
                        <Typography level="body-sm" sx={{ color: 'neutral.500', py: 2, textAlign: 'center' }}>
                          No hay filas. Usá «Agregar otra fila» para comenzar.
                        </Typography>
                      </td>
                    </tr>
                  ) : null}
                  {brands.map((brand, index) => (
                    <tr key={index}>
                      <td style={{ textAlign: 'center' }}>
                        <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                          {index + 1}
                        </Typography>
                      </td>
                      <td>
                        <Input
                          size="sm"
                          variant="soft"
                          color="neutral"
                          placeholder="Nombre"
                          value={brand.name}
                          onChange={(e) => updateBrand(index, { name: e.target.value })}
                          slotProps={{ input: inputSlotPadding }}
                          sx={{ width: '100%', minWidth: 160, fontSize: 'sm' }}
                        />
                      </td>
                      <td>
                        <Textarea
                          size="sm"
                          variant="soft"
                          color="neutral"
                          minRows={1}
                          maxRows={3}
                          placeholder="Descripción"
                          value={brand.description}
                          onChange={(e) => updateBrand(index, { description: e.target.value })}
                          slotProps={{ textarea: textareaSlotPadding }}
                          sx={{ width: '100%', minWidth: 200, fontSize: 'sm' }}
                        />
                      </td>
                      <td>
                        <Input
                          size="sm"
                          variant="soft"
                          color="neutral"
                          placeholder="https://..."
                          value={brand.logo}
                          onChange={(e) => updateBrand(index, { logo: e.target.value })}
                          slotProps={{ input: inputSlotPadding }}
                          sx={{ width: '100%', minWidth: 160, fontSize: 'sm' }}
                        />
                      </td>
                      <td>
                        {brand.logo ? (
                          <Box
                            component="img"
                            src={brand.logo}
                            alt=""
                            sx={{
                              height: 36,
                              width: 36,
                              objectFit: 'contain',
                              mt: 0.25,
                              borderRadius: 'xs',
                              bgcolor: 'background.level1',
                            }}
                            onError={(err) => {
                              (err.target as HTMLImageElement).style.visibility = 'hidden';
                            }}
                          />
                        ) : null}
                      </td>
                      <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                        <IconButton
                          type="button"
                          size="sm"
                          variant="plain"
                          color="danger"
                          onClick={() => handleRemoveBrand(index)}
                          aria-label="Eliminar fila"
                        >
                          <CloseIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Box>

            <Button type="button" variant="outlined" color="neutral" onClick={handleAddRow} sx={{ mt: 1.5 }}>
              + Agregar otra fila
            </Button>

            <Stack direction="row" spacing={1} justifyContent="space-between" sx={{ mt: 2 }}>
              <Button type="submit" color="primary" loading={loading}>
                Crear marcas
              </Button>
              <Button type="button" variant="outlined" color="neutral" onClick={() => navigate('/brands')}>
                Cancelar
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Sheet>
    </Box>
  );
};

export default BulkBrandCreator;
